/**
 * Luật thuần của luồng hỏi–đáp (port `lich-su.ts`, `luu-so-lieu.ts` và phần luật của `useAskTurns.ts` native).
 * Không React, không DOM — test được bằng `node:test`.
 */
import type { AskDisplay, AskResult, LuotLichSu } from '../types.ts';

/** Số lượt gửi lên làm lịch sử — bằng `MAX_LUOT_NHO` phía trợ lý: lời nhắc chỉ đọc bốn lượt cuối. */
export const MAX_LUOT_LICH_SU = 4;

/**
 * Tên chặng "đọc thành tiếng" do MÁY CHỦ đặt — màn chat với bot ẩn/hiện chữ theo trạng thái chặng này. Là chữ của máy
 * chủ (khớp nguyên văn), không phải chuỗi giao diện.
 */
export const CHANG_DOC_THANH_TIENG = 'đọc thành tiếng';

/** Ba cấp văn xuôi — chỉ MÃ; nhãn ở catalog `troLyAiHoiCapVan`. */
export const CAP_VAN = ['gon', 'phan_tich', 'khuyen_nghi'] as const;
export type CapVan = (typeof CAP_VAN)[number];

/** Bốn nấc độ dài, TRỰC GIAO với cấp văn: cấp quyết định viết về cái gì, độ dài quyết định viết bao nhiêu. */
export const DO_DAI = ['ngan', 'vua', 'chi_tiet', 'day_du'] as const;
export type DoDai = (typeof DO_DAI)[number];

export const CHON_BIEU_DO = ['tu_dong', 'co', 'khong'] as const;
export type ChonBieuDo = (typeof CHON_BIEU_DO)[number];

/**
 * Ba bậc tra web. Bậc TẮT nghĩa là không chạy: `tu_dong` nổ ở mọi lượt kho tri thức không khớp và cộng ~6,8 giây vào độ
 * trễ — một công tắc TẮT mà vẫn chạy sáu giây rưỡi là một công tắc nói dối.
 */
export const BAC_WEB = ['khong', 'tu_dong', 'chi_web'] as const;
export type BacWeb = (typeof BAC_WEB)[number];

export const laBac = <T extends string>(ds: readonly T[], v: string): v is T => (ds as readonly string[]).includes(v);

/** Giá trị cũ không còn trong danh sách (ví dụ `co` của tra web) rơi về mặc định. */
export function chuanHoa<T extends string>(ds: readonly T[], v: string, macDinh: T): T {
  return laBac(ds, v) ? v : macDinh;
}

/** Hình dạng tối thiểu của một lượt để dựng lịch sử. */
export interface LuotDeGhep {
  question: string;
  result?: Pick<AskResult, 'answer' | 'kind' | 'sql' | 'kichBan'>;
  error?: { code: string; message: string };
}

/**
 * `KHUON_THIEU_THAM_SO` hiện ra như một câu hỏi lại nhưng đi về dạng `error`. Bỏ nó khỏi lịch sử thì câu người dùng trả
 * lời ("2026") đến máy chủ mà không còn câu gốc lẫn câu hỏi lại. `THU_TUC_THIEU_THAM_SO` thì KHÔNG: thủ tục không có
 * tham số ấy, trả lời gì cũng không gỡ được.
 *
 * Mã lỗi đi qua backend platform mang tiền tố `TRO_LY_AI_` — nhận cả hai dạng.
 */
export function laLuotHoiLai(t: LuotDeGhep): boolean {
  const ma = t.error?.code ?? '';
  return t.result?.kind === 'clarify' || ma === 'KHUON_THIEU_THAM_SO' || ma === 'TRO_LY_AI_KHUON_THIEU_THAM_SO';
}

/**
 * Lượt trong phiên → lịch sử gửi lên. Chỉ CÂU HỎI và LỜI VĂN (kèm câu lệnh và trạng thái kịch bản), không kèm bảng số:
 * thứ cần giải là đại từ, chủ ngữ nằm trong câu hỏi trước chứ không trong 200 dòng kết quả.
 *
 * `kichBan` gửi NGUYÊN KHỐI NGOÀI (có `xong`), kể cả khi đã xong: máy chủ duyệt ngược và dừng ở lượt gần nhất còn mang
 * trạng thái — tự cắt lượt cuối là tua lại về giữa chừng.
 */
export function lichSuTrongPhien(turns: readonly LuotDeGhep[]): LuotLichSu[] {
  return turns.flatMap((t) => {
    const hoiLai = laLuotHoiLai(t);
    const answer = t.result?.answer || (hoiLai ? t.error?.message : '') || '';
    if (!answer) return [];
    return [
      {
        question: t.question,
        answer,
        ...(hoiLai ? { hoiLai: true } : {}),
        ...(t.result?.sql ? { sql: t.result.sql } : {}),
        ...(t.result?.kichBan ? { kichBan: t.result.kichBan } : {}),
      },
    ];
  });
}

/** Lượt đã lưu đứng TRƯỚC lượt trong phiên (theo thời gian), rồi cắt còn `MAX_LUOT_LICH_SU` lượt cuối. */
export function ghepLichSu(daLuu: readonly LuotLichSu[], trongPhien: readonly LuotLichSu[]): LuotLichSu[] {
  return [...daLuu, ...trongPhien].slice(-MAX_LUOT_LICH_SU);
}

interface TinNhan {
  id: string;
  author: string;
  text: string;
}

/**
 * Ghép tin nhắn ĐÃ LƯU (mỗi lượt hai dòng `user` rồi `bot`) thành các lượt `{question, answer}`.
 *
 * `bo` là tin nhắn CHÍNH PHIÊN NÀY vừa ghi — chúng đã nằm trong `turns`; không loại ra thì mỗi lượt vừa hỏi bị đếm hai
 * lần. Dòng `bot` mồ côi (lượt hỏng nửa chừng, lời chào) không có câu hỏi để gắn — bỏ, đừng gắn nhầm sang lượt trước.
 */
export function lichSuTuTinNhan(ms: readonly TinNhan[], bo: ReadonlySet<string>): LuotLichSu[] {
  const ra: LuotLichSu[] = [];
  for (const m of ms) {
    if (bo.has(m.id)) continue;
    const chu = (m.text ?? '').trim();
    if (!chu) continue;
    if (m.author === 'user') {
      ra.push({ question: chu, answer: '' });
      continue;
    }
    const cuoi = ra[ra.length - 1];
    if (cuoi && !cuoi.answer) cuoi.answer = chu;
  }
  return ra.filter((l) => l.answer);
}

/**
 * Trần phần bảng lưu vào `payload` — theo BYTE chứ không theo dòng: cùng 200 dòng, kết quả gộp nhóm là 2 KB còn danh
 * sách 16 cột là 90 KB.
 */
export const TRAN_BYTE_BANG = 128 * 1024;

export interface SoLieuDaLuu {
  rows: Record<string, unknown>[];
  columns: string[];
  nhanCot?: Record<string, string>;
  display?: AskDisplay;
  veBang?: boolean;
  /** Lượt lưu TRƯỚC khi có `veBang`. Chỉ để đọc lại. */
  hoiDanhSach?: boolean;
  /** Số dòng LƯU được. Vắng = lưu trọn. */
  rowsLuu?: number;
  /** TÓM TẮT các nguồn của lượt (từ hai nguồn trở lên) — bảng lưu lại chỉ là của nguồn đầu tiên. */
  nguon?: { ten: string; soDong: number; loi: string }[];
}

/** Hợp mọi cột có thể nằm trên trục — của dạng đang vẽ VÀ của mọi dạng hợp lệ. */
function cotBieuDo(d: AskDisplay | undefined): string[] {
  if (!d) return [];
  const ra = new Set<string>();
  const them = (...c: (string | null | undefined)[]) => {
    for (const x of c) if (x) ra.add(x);
  };
  them(d.x, d.y, d.loat, d.kich_thuoc, ...(d.chi_tieu ?? []));
  for (const t of Object.values(d.truc ?? {})) them(t.x, t.y, t.loat);
  return [...ra];
}

const co = (v: unknown): number => new TextEncoder().encode(JSON.stringify(v)).length;

/**
 * Gói số liệu của một lượt để lưu vào `payload` tin nhắn, đủ để lượt đọc lại vẽ được biểu đồ và bảng.
 *
 * Lượt KHÔNG vẽ bảng: chỉ giữ cột trục (200 dòng × 16 cột 90 KB → 2 cột trục ~6 KB). Lượt CÓ bảng: lưu trọn cột, cắt
 * dòng cho vừa `TRAN_BYTE_BANG` và ghi `rowsLuu` để màn đọc lại nói ra.
 */
export function goiSoLieu(r: AskResult | undefined): SoLieuDaLuu | null {
  const goc = r?.rows;
  if (!r || !goc?.length) return null;

  const nguon =
    (r.nguon?.length ?? 0) > 1 ? r.nguon?.map((n) => ({ ten: n.ten, soDong: n.rowCount, loi: n.loi })) : undefined;

  if (!(r.veBang ?? r.hoiDanhSach)) {
    const cot = cotBieuDo(r.display);
    if (!cot.length) return null;
    const rows = goc.map((h) => Object.fromEntries(cot.map((c) => [c, h[c]])));
    return { rows, columns: cot, display: r.display, veBang: false, nguon };
  }

  const columns = r.columns ?? Object.keys(goc[0] ?? {});
  const chung = { columns, nhanCot: r.nhanCot, display: r.display, veBang: true, nguon };
  if (co({ ...chung, rows: goc }) <= TRAN_BYTE_BANG) return { ...chung, rows: goc };

  // Ước số dòng vừa trần rồi lùi dần — nhanh hơn cắt từng dòng, vẫn chính xác nhờ vòng kiểm lại.
  let n = Math.max(1, Math.floor((TRAN_BYTE_BANG / co({ ...chung, rows: goc })) * goc.length));
  while (n > 1 && co({ ...chung, rows: goc.slice(0, n) }) > TRAN_BYTE_BANG) n = Math.floor(n * 0.9);
  return { ...chung, rows: goc.slice(0, n), rowsLuu: n };
}

/** Lọc danh sách nguồn đã lưu với danh sách thật — id chết làm mọi lời gọi trả 404. */
export function nguonConSong(daChon: readonly string[], ds: readonly { id: string }[]): string[] {
  return daChon.filter((id) => ds.some((x) => x.id === id));
}

/**
 * Nguồn đem hỏi: lựa chọn còn sống, hoặc nguồn đầu khi chưa chọn gì — phép đoán HIỆN RA trong ô chọn, nên người dùng
 * thấy ngay mình đang hỏi ở đâu.
 */
export function nguonDemHoi(daChon: readonly string[], ds: readonly { id: string }[]): string[] {
  const song = nguonConSong(daChon, ds);
  if (song.length) return song;
  return ds[0] ? [ds[0].id] : [];
}
